import * as React from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { Button, Theme, useUiLabel } from '@canvas-logic/clp-shared-ui';

import { Basic3dLayoutParams } from '@/ui-kit';
import { UiBuilder } from '@/UiBuilder/UiBuilder';

import {
  getCanvasLogicBrandingLink,
  isCanvasLogicBrandingEnabled,
  isInfoEnabled,
  isShareEnabled,
  isWebArEnabled,
} from '../../domain/UiSpecification';
import NotificationContainer from '../Notification/NotificationContainer';
import { CanvasLogicBranding } from '../CanvasLogicBranding/CanvasLogicBranding';
import { useBootstrapper } from '../../contexts/BootstrapperContext';
import SheetRouter from '../SheetRouter/SheetRouter';
import { useSheetRouter } from '../SheetRouter/SheetRouterContext';
import { useConfiguratorRoute } from '@/hooks/useConfiguratorRoute';
import { embeddingService } from '@/ui-kit/services/embedding/EmbeddingService';
import { RequestQuoteButton } from '@/ui-kit/components/RequestQuote/RequestQuoteButton';
import PresetValidationBanner from '@/ui-kit/components/PresetValidationBanner/PresetValidationBanner';
import { ViewportErrorBoundary } from '@/ui-kit/components/ViewportErrorBoundary/ViewportErrorBoundary';
import { ViewportErrorFallback } from '@/ui-kit/components/ViewportErrorBoundary/ViewportErrorFallback';

import { useEffect, useState } from 'react';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';
import Actions from '@/ui-kit/components/Actions/Actions';
import { PortalProvider } from '@/ui-kit/components/PortalContext';
import { formatSelectedPrice } from '@/ui-kit/services/price-selection';

function Basic3dLayout(_props: Basic3dLayoutParams) {
  const configureLabel = useUiLabel('ui.button.configure');
  const { hideConfigurationControls, background: _background, configuratorMode, hide3d } = useConfiguratorRoute();
  const { bootstrapper, canvasRef, canvasElement } = useBootstrapper();
  const { openMenu } = useSheetRouter();
  const [fullscreen, setFullscreen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const configuratorUI = bootstrapper?.configuratorUI;
  const hiddenControls = configuratorUI?.layout?.controlsHidden || hideConfigurationControls;
  const sidebarMaxWidth = configuratorUI?.layout?.sidebarMaxWidth || '4-vertical-cards';

  const showPrice = bootstrapper?.hasPrice;
  const contactUsService = bootstrapper?.contactUsService;
  const showRequestQuote = !fullscreen && !showPrice && bootstrapper?.isInCatalog && contactUsService?.isEnabled();

  useEffect(() => {
    return embeddingService.subscribeOnAttributeValueChange((event) => {
      const { componentKey, attributeKey, attributeValue } = event.payload;
      const sdk = bootstrapper?.sdk;
      if (sdk?.composite) {
        sdk.api.setAttributeValue(componentKey, attributeKey, attributeValue).then(() => {
          bootstrapper?.handleProductChanges();
        });
      }
    }, []);
  }, [bootstrapper]);

  useEffect(() => {
    // ADR-0066: live slot-content sync from the preset editor. Component
    // attributes keep flowing through set_attribute_value; here only the slot
    // placements (and custom values) are replaced with the editor's snapshot.
    return embeddingService.subscribeOnConfigurationStateChange((event) => {
      const sdk = bootstrapper?.sdk;
      if (!sdk?.composite) return;
      const api = sdk.api as ICompositeProductApi;
      const { state } = event.payload;
      (async () => {
        for (const slot of api.getSlots()) {
          for (const item of [...slot.getItems()]) {
            slot.removeItem(item.id);
          }
        }
        await api.applySerializedState({
          version: state.version,
          components: {},
          custom: state.custom ?? {},
          ...(state.meta ? { meta: state.meta } : {}),
          slots: state.slots ?? {},
        });
        bootstrapper?.handleProductChanges();
      })().catch((e) => console.error('Failed to apply configuration state from host', e));
    }, []);
  }, [bootstrapper]);

  useEffect(() => {
    if (configuratorMode === 'preview') {
      document.getElementById('root')?.style.setProperty('--color-background', 'transparent');
    }
  }, [configuratorMode]);

  return (
    <PortalProvider portalElement={portalContainer}>
      <main
        className={classNames('root', {
          'root--no3d': hide3d,
          ['basic-3d-layout--controls-hidden']: hiddenControls,
        })}
        data-sidebar-max-width={sidebarMaxWidth}
      >
        {configuratorUI && <Theme themeConfig={configuratorUI.theme} />}
        <NotificationContainer service={bootstrapper?.notificationService} />
        {bootstrapper?.presetValidation && <PresetValidationBanner validation={bootstrapper.presetValidation} />}
        {!hide3d &&
          (bootstrapper?.view3dError ? (
            <div
              className={classNames(
                'viewport__container',
                configuratorMode === 'preview' && 'viewport__container--preview-mode',
              )}
            >
              <ViewportErrorFallback onRetry={() => window.location.reload()} />
            </div>
          ) : (
            <ViewportErrorBoundary>
              <div
                className={classNames(
                  'viewport__container',
                  configuratorMode === 'preview' && 'viewport__container--preview-mode',
                )}
              >
                <div className={'viewport-frame'} ref={setPortalContainer}>
                  <div
                    id="viewer"
                    className={classNames('viewport', configuratorMode === 'preview' && 'viewport--preview-mode')}
                  >
                    <canvas
                      ref={canvasRef}
                      className={classNames('canvas', configuratorMode === 'preview' && 'canvas--preview-mode')}
                    />
                  </div>
                  <div className={classNames('viewport__config-button-container')}>
                    <Button variant="ternary" iconStart="settings" onClick={openMenu}>
                      {configureLabel}
                    </Button>
                  </div>
                  {bootstrapper && (
                    <div className={classNames('viewport__price-container')}>
                      {showPrice && (
                        <Button
                          data-price-button
                          variant="primary"
                          iconStart="shoppingBag"
                          onClick={bootstrapper.addToCart}
                        >
                          {formatSelectedPrice(
                            bootstrapper.totalPrice,
                            bootstrapper.selectedCurrency,
                            bootstrapper.effectiveLocale,
                          )}
                        </Button>
                      )}
                      {showRequestQuote && <RequestQuoteButton contactUsService={contactUsService!} />}
                    </div>
                  )}

                  {/* `configuratorUI` is non-optional on Bootstrapper, so it is present
                      whenever `bootstrapper` is — testing it only narrows the type. */}
                  {bootstrapper && configuratorUI && isCanvasLogicBrandingEnabled(configuratorUI) && (
                    <div className={'viewport__branding-container'}>
                      <CanvasLogicBranding url={getCanvasLogicBrandingLink(configuratorUI)} />
                    </div>
                  )}
                  <div className={'viewport__actions-container'}>
                    {bootstrapper && configuratorUI && !hideConfigurationControls && (
                      <Actions
                        bootstrapper={bootstrapper}
                        hasAr={isWebArEnabled(configuratorUI)}
                        hasInfo={isInfoEnabled(configuratorUI)}
                        hasShare={isShareEnabled(configuratorUI)}
                      />
                    )}
                  </div>
                  {!hideConfigurationControls && (
                    <div className={classNames('viewport__fullscreen-container')}>
                      <Button
                        variant="ternary"
                        iconStart="expandViewport"
                        onClick={async () => {
                          if (!document.fullscreenElement && canvasElement) {
                            await canvasElement.parentElement?.parentElement?.requestFullscreen();
                            setFullscreen(true);
                          } else if (document.exitFullscreen) {
                            await document.exitFullscreen();
                            setFullscreen(false);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </ViewportErrorBoundary>
          ))}
        {!hiddenControls && (
          <div className={classNames('viewport__controls-container')}>
            <div className={classNames('viewport__bottom-sheet-container')}>
              <SheetRouter />
            </div>
            <div className={classNames('viewport__controls-wrapper')}>
              <div className={'viewport__controls'} style={hide3d ? { width: '100%' } : {}}>
                {bootstrapper && configuratorUI && (
                  <UiBuilder
                    configuratorUI={configuratorUI}
                    bootstrapper={bootstrapper}
                    configuration={bootstrapper.productVariant}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </PortalProvider>
  );
}

export default observer(Basic3dLayout);
